import { Flower, Truck } from "@/components/icons";
import { cn } from "@/lib/utils";
import type { ShippingLabelViewModel } from "@/modules/chat-tools/domain/build-shipping-label";
import { formatLabelAmount } from "@/modules/chat-tools/domain/build-shipping-label";
import { forwardRef, type ReactNode } from "react";

type ShippingLabelCardProps = {
  label: ShippingLabelViewModel;
  className?: string;
  id?: string;
};

function LabelPill({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-full bg-secondary px-3 py-1 font-medium text-secondary-foreground text-sm",
        className,
      )}
    >
      {children}
    </span>
  );
}

function LabelField({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-muted px-4 py-3 text-base text-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

function LabelBar({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-secondary px-4 py-2.5 text-center font-medium text-secondary-foreground">
      {children}
    </div>
  );
}

export const ShippingLabelCard = forwardRef<
  HTMLDivElement,
  ShippingLabelCardProps
>(function ShippingLabelCard({ label, className, id }, ref) {
  return (
    <div
      ref={ref}
      id={id}
      className={cn(
        "flex w-full max-w-[20rem] flex-col gap-3 rounded-3xl border border-border bg-card p-4 shadow-md print:max-w-[80mm] print:rounded-none print:border-0 print:p-3 print:shadow-none",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <LabelPill>ຜູ້ຝາກ</LabelPill>
        <span className="font-medium text-lg tabular-nums tracking-wide">
          {label.senderPhone}
        </span>
      </div>

      <div className="flex items-start gap-3">
        <LabelPill>ຜູ້ຮັບ</LabelPill>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-lg leading-snug">{label.recipientName}</p>
          <p className="mt-1.5 font-semibold text-2xl tabular-nums leading-none tracking-wide">
            {label.recipientPhone}
          </p>
        </div>
      </div>

      <LabelBar>ປະເພດ: {label.categoryLabel}</LabelBar>

      <div className="flex flex-col gap-1.5">
        {label.addressDetail ? (
          <p className="text-lg leading-relaxed">{label.addressDetail}</p>
        ) : null}
        <div className="flex items-start gap-2">
          <Truck className="mt-1 size-5 shrink-0 text-secondary" />
          <div className="min-w-0 flex-1">
            {label.branchName ? (
              <>
                <p className="font-bold text-xl leading-tight tracking-tight">
                  ສາຂາ {label.branchName}
                </p>
                <p className="mt-1 font-medium text-base leading-snug">
                  {label.carrierName}
                </p>
              </>
            ) : (
              <p className="font-bold text-lg leading-snug">{label.carrierName}</p>
            )}
          </div>
        </div>
        {label.localityLine ? (
          <p className="text-lg leading-relaxed">{label.localityLine}</p>
        ) : null}
      </div>

      {label.isCod ? (
        <>
          <LabelBar>#ເກັບ COD</LabelBar>
          <div className="flex items-center gap-2 font-medium text-secondary">
            <span className="shrink-0">ລາຄາ:</span>
            <LabelField className="flex-1 py-2 text-center font-display text-2xl tabular-nums">
              {formatLabelAmount(label.codAmount)}
            </LabelField>
            <span className="shrink-0">ກີບ</span>
          </div>
          {label.collectShippingAtDestination ? (
            <p className="text-center font-medium text-secondary text-sm">
              (ເກັບຄ່າສົ່ງປາຍທາງ)
            </p>
          ) : null}
        </>
      ) : null}

      <LabelBar>
        <span className="inline-flex items-center justify-center gap-2">
          <Flower className="size-4 shrink-0" />
          <span>ຂອບໃຈເດີ້ · Thank You</span>
        </span>
      </LabelBar>
    </div>
  );
});
